<template>
  <div class="status-history-page">
    <!-- Header -->
    <div class="header">
      <button class="back-button" @click="goBack">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <h1>{{ $t("stories.my_statuses_history") }}</h1>
      <div class="header-spacer"></div>
    </div>

    <!-- Content -->
    <div class="content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t("stories.loading") }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="statuses.length === 0" class="empty-state">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>{{ $t("stories.no_statuses_yet") }}</p>
        <button class="create-button" @click="navigateToCreate">
          {{ $t("stories.create_first_status") }}
        </button>
      </div>

      <!-- Status List -->
      <div v-else class="status-list">
        <div
          v-for="status in statuses"
          :key="status.id"
          class="status-card"
          :class="{
            expired: status.isExpired,
            rejected: status.statusState === 'rejected',
          }"
        >
          <!-- Status Preview -->
          <div class="status-preview" @click="viewStatus(status)">
            <!-- Text Status -->
            <div v-if="status.type === 'text'" class="text-preview">
              <p>{{ status.textOverlay || status.description || "" }}</p>
            </div>

            <!-- Image/Video Status -->
            <img
              v-else-if="status.thumbnailDeliveryUrl"
              :src="status.thumbnailDeliveryUrl"
              class="media-preview"
              alt="Status preview"
            />

            <!-- Fallback -->
            <div v-else class="preview-placeholder">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </div>

            <!-- Video indicator -->
            <div v-if="status.type === 'video'" class="video-indicator">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            </div>

            <!-- Status badge -->
            <div
              v-if="status.statusState === 'pending'"
              class="status-badge pending"
            >
              {{ $t("stories.pending") }}
            </div>
            <div
              v-else-if="status.statusState === 'rejected'"
              class="status-badge rejected"
            >
              {{ $t("stories.rejected") }}
            </div>
            <div v-else-if="status.isExpired" class="status-badge expired">
              {{ $t("stories.expired") }}
            </div>
          </div>

          <!-- Status Info -->
          <div class="status-info">
            <div class="status-meta">
              <span class="status-type">{{ getTypeLabel(status.type) }}</span>
              <span class="status-date">{{
                formatDate(status.createdAt)
              }}</span>
            </div>

            <div class="status-stats">
              <div class="stat">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <span>{{ status.viewCount || 0 }}</span>
              </div>
              <div class="stat">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  ></path>
                </svg>
                <span>{{ status.reactionCount || 0 }}</span>
              </div>
            </div>

            <!-- Rejection reason -->
            <div
              v-if="status.statusState === 'rejected' && status.rejectionReason"
              class="rejection-reason"
            >
              <strong>{{ $t("stories.reason") }}:</strong>
              {{ status.rejectionReason }}
            </div>

            <!-- Actions -->
            <div class="status-actions">
              <button
                v-if="status.reactionCount > 0"
                class="action-btn"
                @click="viewReactions(status)"
              >
                {{ $t("stories.view_reactions") }}
              </button>
              <button class="action-btn delete" @click="confirmDelete(status)">
                {{ $t("stories.delete") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <Transition name="fade">
      <div
        v-if="showDeleteConfirm"
        class="confirm-dialog-overlay"
        @click="showDeleteConfirm = false"
      >
        <div class="confirm-dialog" @click.stop>
          <h3>{{ $t("stories.delete_status") }}</h3>
          <p>{{ $t("stories.delete_status_confirmation") }}</p>
          <div class="dialog-actions">
            <button class="cancel-btn" @click="showDeleteConfirm = false">
              {{ $t("stories.cancel") }}
            </button>
            <button
              class="delete-btn"
              :disabled="isDeleting"
              @click="deleteStatus"
            >
              <span v-if="isDeleting" class="spinner small"></span>
              <span v-else>{{ $t("stories.delete") }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Story Viewer Modal -->
    <Transition name="fade">
      <div
        v-if="showStoryViewer"
        class="story-viewer-overlay"
        @click="closeStoryViewer"
      >
        <div class="story-viewer-modal" @click.stop>
          <!-- Close Button -->
          <button class="close-viewer-btn" @click="closeStoryViewer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <!-- Story Content -->
          <div class="story-content-wrapper">
            <!-- Text Status -->
            <div
              v-if="viewingStatus?.type === 'text'"
              class="story-text-content"
            >
              <p
                v-html="
                  linkifyText(
                    viewingStatus.textOverlay || viewingStatus.description || ''
                  )
                "
                @click="handleLinkClick"
              ></p>
            </div>

            <!-- Image Status -->
            <img
              v-else-if="
                viewingStatus?.type === 'image' &&
                viewingStatus.mediaDeliveryUrl
              "
              :src="viewingStatus.mediaDeliveryUrl"
              class="story-media-content"
              alt="Status"
            />

            <!-- Video Status -->
            <video
              v-else-if="
                viewingStatus?.type === 'video' &&
                viewingStatus.mediaDeliveryUrl
              "
              ref="storyVideoRef"
              :src="viewingStatus.mediaDeliveryUrl"
              class="story-media-content"
              controls
              autoplay
              playsinline
            ></video>

            <!-- Fallback -->
            <div v-else class="story-placeholder">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p>{{ $t("stories.preview_not_available") }}</p>
            </div>
          </div>

          <!-- Story Info -->
          <div class="story-info-bar">
            <div class="story-stats">
              <span class="stat-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                {{ viewingStatus?.viewCount || 0 }}
              </span>
              <span class="stat-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  ></path>
                </svg>
                {{ viewingStatus?.reactionCount || 0 }}
              </span>
            </div>
            <span class="story-date">{{
              formatDate(viewingStatus?.createdAt)
            }}</span>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Reactions Bottom Sheet -->
    <Transition name="slide-up">
      <div
        v-if="showReactions"
        class="reactions-sheet-overlay"
        @click="showReactions = false"
      >
        <div class="reactions-sheet" @click.stop>
          <div class="sheet-handle"></div>
          <div class="sheet-header">
            <h3>{{ $t("stories.reactions") }}</h3>
            <span class="total-count">{{ reactions.length }}</span>
          </div>
          <div class="reactions-list">
            <div v-if="loadingReactions" class="loading-reactions">
              <div class="spinner"></div>
            </div>
            <div v-else-if="reactions.length === 0" class="no-reactions">
              <p>{{ $t("stories.no_reactions_yet") }}</p>
            </div>
            <div
              v-else
              v-for="reaction in reactions"
              :key="reaction.id"
              class="reaction-item"
            >
              <div class="reaction-user-avatar">
                <img
                  v-if="reaction.user?.image"
                  :src="reaction.user.image"
                  :alt="reaction.user?.name"
                />
                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div class="reaction-user-info">
                <span class="reaction-user-name">{{
                  reaction.user?.name || $t("stories.unknown_user")
                }}</span>
                <span class="reaction-time">{{
                  formatTimeAgo(reaction.createdAt)
                }}</span>
              </div>
              <span class="reaction-emoji">{{
                getReactionEmoji(reaction.type)
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { StoriesApi } from "@/services/storiesApi";

const router = useRouter();
const { t } = useI18n();

// State
const statuses = ref([]);
const isLoading = ref(true);
const showDeleteConfirm = ref(false);
const statusToDelete = ref(null);
const isDeleting = ref(false);
const showReactions = ref(false);
const reactions = ref([]);
const loadingReactions = ref(false);

// Story Viewer State
const showStoryViewer = ref(false);
const viewingStatus = ref(null);
const storyVideoRef = ref(null);

// Methods
function goBack() {
  router.back();
}

function navigateToCreate() {
  router.push("/status/create");
}

function getTypeLabel(type) {
  const labels = {
    text: t("stories.text"),
    image: t("stories.image"),
    video: t("stories.video"),
  };
  return labels[type] || type;
}

function formatDate(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatTimeAgo(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return t("stories.just_now");
    if (diffMin < 60) return t("stories.minutes_ago", { count: diffMin });
    if (diffHour < 24) return t("stories.hours_ago", { count: diffHour });
    if (diffDay < 7) return t("stories.days_ago", { count: diffDay });
    return date.toLocaleDateString();
  } catch {
    return "";
  }
}

function getReactionEmoji(type) {
  const emojiMap = {
    love: "❤️",
    heart: "❤️",
    fire: "🔥",
    clap: "👏",
    like: "👍",
    laugh: "😂",
    wow: "😮",
    sad: "😢",
    angry: "😡",
  };
  return emojiMap[type?.toLowerCase()] || "❤️";
}

/**
 * Converts URLs in text to clickable hyperlinks.
 */
function linkifyText(text) {
  if (!text) return "";

  const escapeHtml = (str) => {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  };

  const urlRegex =
    /https?:\/\/[^\s<>\[\]{}|\\^`"]+|www\.[^\s<>\[\]{}|\\^`"]+/gi;

  let result = "";
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    result += escapeHtml(text.substring(lastIndex, match.index));

    const url = match[0];
    const href = url.startsWith("www.") ? `https://${url}` : url;

    result += `<a href="${escapeHtml(
      href
    )}" target="_blank" rel="noopener noreferrer" class="story-link">${escapeHtml(
      url
    )}</a>`;

    lastIndex = urlRegex.lastIndex;
  }

  result += escapeHtml(text.substring(lastIndex));

  return result;
}

function handleLinkClick(event) {
  if (event.target.tagName === "A") {
    event.stopPropagation();
  }
}

function viewStatus(status) {
  // Open the story viewer modal
  viewingStatus.value = status;
  showStoryViewer.value = true;
}

function closeStoryViewer() {
  // Pause video if playing
  if (storyVideoRef.value) {
    storyVideoRef.value.pause();
  }
  showStoryViewer.value = false;
  viewingStatus.value = null;
}

function confirmDelete(status) {
  statusToDelete.value = status;
  showDeleteConfirm.value = true;
}

async function deleteStatus() {
  if (!statusToDelete.value || isDeleting.value) return;

  isDeleting.value = true;

  try {
    await StoriesApi.deleteStatus(statusToDelete.value.id);
    statuses.value = statuses.value.filter(
      (s) => s.id !== statusToDelete.value.id
    );
    showDeleteConfirm.value = false;
    statusToDelete.value = null;
  } catch (error) {
    console.error("Failed to delete status:", error);
  } finally {
    isDeleting.value = false;
  }
}

async function viewReactions(status) {
  showReactions.value = true;
  loadingReactions.value = true;

  try {
    reactions.value = await StoriesApi.getReactions(status.id);
  } catch (error) {
    console.error("Failed to load reactions:", error);
    reactions.value = [];
  } finally {
    loadingReactions.value = false;
  }
}

async function fetchMyStatuses() {
  isLoading.value = true;

  try {
    // Fetch user's own statuses directly
    const myStatuses = await StoriesApi.getMyStatuses();

    // Normalize the response data
    statuses.value = (myStatuses || []).map((s) => ({
      id: s.id,
      type: s.type,
      textOverlay: s.text_overlay || s.textOverlay,
      description: s.description,
      thumbnailDeliveryUrl: s.thumbnail_delivery_url || s.thumbnailDeliveryUrl,
      mediaDeliveryUrl: s.media_delivery_url || s.mediaDeliveryUrl,
      statusState: s.status_state || s.statusState || "published",
      createdAt: s.created_at || s.createdAt,
      isExpired: s.is_expired ?? s.isExpired ?? false,
      viewCount: s.view_count ?? s.viewCount ?? 0,
      reactionCount: s.reaction_count ?? s.reactionCount ?? 0,
      rejectionReason: s.rejection_reason || s.rejectionReason,
    }));
  } catch (error) {
    console.error("Failed to fetch statuses:", error);
    statuses.value = [];
  } finally {
    isLoading.value = false;
  }
}

// Lifecycle
onMounted(() => {
  fetchMyStatuses();
});
</script>

<style scoped>
.status-history-page {
  min-height: 100vh;
  background: var(--surface, #fffbfe);
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--outline-variant, #cac4d0);
  background: var(--surface, #fffbfe);
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-button {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--on-surface, #1c1b1f);
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.back-button:hover {
  background: var(--surface-container, #f3edf7);
}

.header h1 {
  flex: 1;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: var(--on-surface, #1c1b1f);
  margin: 0;
}

.header-spacer {
  width: 40px;
}

/* Content */
.content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: var(--on-surface-variant, #49454f);
}

.loading-state p {
  margin-top: 16px;
  font-size: 14px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: var(--on-surface-variant, #49454f);
}

.empty-state p {
  margin-top: 16px;
  font-size: 16px;
}

.create-button {
  margin-top: 24px;
  padding: 12px 24px;
  background: #00c444;
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.3);
}

.create-button:hover {
  background: #00a83a;
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.4);
  transform: translateY(-1px);
}

/* Status List */
.status-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-card {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: var(--surface-container, #f3edf7);
  border-radius: 16px;
  transition: all 0.2s ease;
}

.status-card.expired {
  opacity: 0.7;
}

.status-card.rejected {
  border: 1px solid var(--error, #b3261e);
}

/* Status Preview */
.status-preview {
  width: 80px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
}

.text-preview {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #00c444, #00a83a);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.text-preview p {
  font-size: 10px;
  color: white;
  text-align: center;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
}

.media-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder {
  width: 100%;
  height: 100%;
  background: var(--surface-container-highest, #e6e0e9);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--on-surface-variant, #49454f);
}

.video-indicator {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.status-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}

.status-badge.pending {
  background: #00c444;
  color: white;
}

.status-badge.rejected {
  background: var(--error, #b3261e);
  color: white;
}

.status-badge.expired {
  background: var(--outline, #79747e);
  color: white;
}

/* Status Info */
.status-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-type {
  font-size: 12px;
  font-weight: 600;
  color: #00c444;
  text-transform: uppercase;
}

.status-date {
  font-size: 12px;
  color: var(--on-surface-variant, #49454f);
}

.status-stats {
  display: flex;
  gap: 16px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--on-surface-variant, #49454f);
}

.rejection-reason {
  font-size: 12px;
  color: var(--error, #b3261e);
  padding: 8px;
  background: rgba(179, 38, 30, 0.1);
  border-radius: 8px;
}

.status-actions {
  display: flex;
  gap: 8px;
  margin-top: auto;
}

.action-btn {
  padding: 8px 16px;
  background: transparent;
  border: 1px solid var(--outline, #79747e);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--on-surface, #1c1b1f);
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--surface-container-high, #ece6f0);
}

.action-btn.delete {
  color: var(--error, #b3261e);
  border-color: var(--error, #b3261e);
}

.action-btn.delete:hover {
  background: rgba(179, 38, 30, 0.1);
}

/* Spinner */
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--outline-variant, #cac4d0);
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner.small {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Confirm Dialog */
.confirm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
}

.confirm-dialog {
  background: var(--surface, #fffbfe);
  border-radius: 16px;
  padding: 24px;
  max-width: 320px;
  width: 100%;
}

.confirm-dialog h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--on-surface, #1c1b1f);
  margin-bottom: 8px;
}

.confirm-dialog p {
  font-size: 14px;
  color: var(--on-surface-variant, #49454f);
  margin-bottom: 24px;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.dialog-actions button {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
}

.cancel-btn {
  background: transparent;
  border: 1px solid var(--outline, #79747e);
  color: var(--on-surface, #1c1b1f);
}

.cancel-btn:hover {
  background: var(--surface-container, #f3edf7);
}

.delete-btn {
  background: var(--error, #b3261e);
  border: none;
  color: white;
}

.delete-btn:hover:not(:disabled) {
  background: #9c2018;
}

.delete-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Reactions Sheet */
.reactions-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  z-index: 1000;
}

.reactions-sheet {
  background: var(--surface, #fffbfe);
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
}

.sheet-handle {
  width: 40px;
  height: 4px;
  background: var(--outline-variant, #cac4d0);
  border-radius: 2px;
  margin: 12px auto 8px;
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 16px;
  border-bottom: 1px solid var(--outline-variant, #cac4d0);
}

.sheet-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--on-surface, #1c1b1f);
}

.total-count {
  font-size: 14px;
  color: var(--on-surface-variant, #49454f);
}

.reactions-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.loading-reactions,
.no-reactions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: var(--on-surface-variant, #49454f);
}

.reaction-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.reaction-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--surface-container-highest, #e6e0e9);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: var(--on-surface-variant, #49454f);
}

.reaction-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reaction-user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.reaction-user-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--on-surface, #1c1b1f);
}

.reaction-time {
  font-size: 12px;
  color: var(--on-surface-variant, #49454f);
}

.reaction-emoji {
  font-size: 24px;
  padding: 8px;
  background: var(--surface-container-highest, #e6e0e9);
  border-radius: 50%;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

/* Story Viewer Modal */
.story-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}

.story-viewer-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  background: #000;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.close-viewer-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.2s ease;
}

.close-viewer-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

.story-content-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  max-height: 70vh;
  overflow: hidden;
}

.story-text-content {
  width: 100%;
  height: 100%;
  min-height: 400px;
  background: linear-gradient(135deg, #00c444, #00a83a);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.story-text-content p {
  font-size: 24px;
  font-weight: 500;
  color: white;
  text-align: center;
  line-height: 1.5;
  word-break: break-word;
}

.story-text-content p :deep(.story-link),
.story-text-content p a {
  color: #60a5fa;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.story-text-content p :deep(.story-link):hover,
.story-text-content p a:hover {
  color: #93c5fd;
}

.story-media-content {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.story-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.5);
  padding: 48px;
}

.story-placeholder p {
  font-size: 14px;
}

.story-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.8);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.story-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
}

.stat-item svg {
  opacity: 0.8;
}

.story-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

/* RTL Support for Story Viewer */
[dir="rtl"] .close-viewer-btn {
  right: auto;
  left: 12px;
}
</style>
